import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import TableBody from "@mui/material/TableBody";
import Pagination from "@mui/material/Pagination";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import axios from "axios";
import API_GATEWAY_URL from "../../config";
import moment from "moment";
import "./ExecutionRecords.css";

import palm_count from '../../assets/palm_count.png';
import palm_canopy_size from '../../assets/palm_canopy_size.png';
import palm_distance from '../../assets/palm_distance.png';
import palm_classification from '../../assets/palm_classification.png';

const toolIcons = {
  'Palm Location': palm_count,
  'Palm Canopy Size': palm_canopy_size,
  'Palm Distance': palm_distance,
  'Palm Yellowish Classification': palm_classification
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgb(150, 150, 150)",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    // Removed maxWidth and ellipsis to allow natural text wrapping
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

function formatGMTDate(inputDateStr) {
  const inputDate = new Date(inputDateStr);
  const gmtOffset = inputDate.getTimezoneOffset(); // Get the offset in minutes

  // Add the GMT offset to the input date to convert it to GMT time
  inputDate.setMinutes(inputDate.getMinutes() - gmtOffset);

  const options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  const formattedDate = inputDate.toLocaleString("en-US", options);

  return formattedDate;
}

function renderToolIcons(inputString) {
  if (!inputString) return null;
  let toolStr = inputString;
  if (Array.isArray(inputString)) {
    toolStr = inputString.join(",");
  }
  const tools = toolStr.split(",").map((word) => {
    return word.trim()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  });

  return (
    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
      {tools.map((tool, index) => (
        <Tooltip title={tool} key={index} arrow>
          <img
            src={toolIcons[tool]}
            alt={tool}
            style={{ width: '48px', height: '48px', objectFit: 'contain' }}
          />
        </Tooltip>
      ))}
    </div>
  );
}

function formatRemark(remark) {
  if (!remark) return "";

  // Extract user-friendly error messages from tracebacks
  if (remark.includes("Alignment Error:")) {
    const match = remark.match(/(Alignment Error:\s*Boundary[^\n\r]*)/);
    if (match) {
      return match[1].trim();
    }
    // Fallback if the error message is different
    const fallback = remark.match(/(Alignment Error:[^\n\r]*)/);
    if (fallback) {
      return fallback[1].trim();
    }
  }

  if (remark.includes("Boundary Error:")) {
    const match = remark.match(/(Boundary Error:.*)/);
    if (match) {
      return match[1].trim();
    }
  }

  let isAdmin = false;
  try {
    const token = localStorage.getItem("palm_care_token");
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      isAdmin = payload.user && (payload.user.is_admin === 1 || payload.user.is_admin === "1" || payload.user.is_admin === true);
    }
  } catch (e) {
    console.error("Failed to parse token for admin check", e);
  }

  if (!isAdmin && (remark.includes("Traceback") || remark.includes("Azure"))) {
    return "Internal System Error (Contact Admin)";
  }
  return remark;
}

function hasMaxRetries(row, data) {
  try {
    if (row.parameters) {
      const params = typeof row.parameters === "string" ? JSON.parse(row.parameters) : row.parameters;
      if (params.retries >= 1) return true;
    }
  } catch (e) {
    // ignore error
  }

  if (Array.isArray(data)) {
    const rowIndex = data.findIndex(r => r.id === row.id);
    if (rowIndex > 0) {
      for (let i = 0; i < rowIndex; i++) {
        const newerJob = data[i];
        if (newerJob.estate === row.estate && newerJob.block === row.block && newerJob.selected_tools === row.selected_tools) {
          return true;
        }
      }
    }
  }
  return false;
}

function CustomizedTables({
  data,
  onClickDownload,
  onClickViewResults,
  handleRetry,
  downloadingRows,
  retryingJobs,
  isLoading,
}) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell>Created On</StyledTableCell>
            <StyledTableCell>Estate</StyledTableCell>
            <StyledTableCell>Block</StyledTableCell>
            <StyledTableCell>Size</StyledTableCell>
            <StyledTableCell>State</StyledTableCell>
            <StyledTableCell>Selected Tools</StyledTableCell>
            <StyledTableCell>Remark</StyledTableCell>
            <StyledTableCell>Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <StyledTableCell colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                <CircularProgress />
              </StyledTableCell>
            </TableRow>
          ) : Array.isArray(data) && data.length > 0 ? (
            data.map((row) => (
              <StyledTableRow key={row.id}>
                <StyledTableCell>
                  {formatGMTDate(row.created_at)}
                </StyledTableCell>
                <StyledTableCell>{row.estate}</StyledTableCell>
                <StyledTableCell>{row.block}</StyledTableCell>
                <StyledTableCell>{row.size}</StyledTableCell>
                <StyledTableCell>{row.state}</StyledTableCell>
                <StyledTableCell>
                  {renderToolIcons(row.selected_tools)}
                </StyledTableCell>
                <StyledTableCell>
                  {row.state.toLowerCase() === "error" && row.remark ? (
                    <Tooltip title={formatRemark(row.remark)} arrow>
                      <div style={{ maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "help" }}>
                        {formatRemark(row.remark)}
                      </div>
                    </Tooltip>
                  ) : ""}
                </StyledTableCell>
                <StyledTableCell>
                  {row.state.toLowerCase() === "completed" && (
                    <Button
                      style={{
                        backgroundColor: "rgb(199, 244, 205)",
                        color: "rgb(11, 85, 22)",
                        fontWeight: 550,
                        marginRight: "5px",
                        width: "80px",
                        fontSize: "0.75rem",
                        padding: "6px 8px",
                      }}
                      onClick={() => onClickDownload(data, row.id)}
                    >
                      {downloadingRows[row.id] ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Download"
                      )}
                    </Button>
                  )}
                  <Button
                    style={{
                      backgroundColor: "rgb(199, 224, 244)",
                      color: "rgb(11, 85, 122)",
                      fontWeight: 550,
                      width: "95px",
                      marginRight: "5px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      fontSize: "11px",
                      padding: "4px 6px",
                    }}
                    onClick={() => onClickViewResults(row.id)}
                  >
                    View Results
                  </Button>
                  {row.state.toLowerCase() === "error" && (
                    <Button
                      style={{
                        backgroundColor:
                          moment().diff(moment(row.created_at), "days") > 1 || hasMaxRetries(row, data)
                            ? "grey"
                            : "rgb(244, 199, 199)",
                        color:
                          moment().diff(moment(row.created_at), "days") > 1 || hasMaxRetries(row, data)
                            ? "darkgrey"
                            : "rgb(122, 11, 11)",
                        fontWeight: 550,
                        width: "80px",
                        fontSize: "0.75rem",
                        padding: "6px 8px",
                        cursor:
                          retryingJobs[row.id] || moment().diff(moment(row.created_at), "days") > 1 || hasMaxRetries(row, data)
                            ? "not-allowed"
                            : "pointer",
                      }}
                      onClick={() => handleRetry(row)}
                      disabled={
                        retryingJobs[row.id] || moment().diff(moment(row.created_at), "days") > 1 || hasMaxRetries(row, data)
                      }
                    >
                      {retryingJobs[row.id] ? "Retried" : "Retry"}
                    </Button>
                  )}
                </StyledTableCell>
              </StyledTableRow>
            ))
          ) : (
            <TableRow>
              <StyledTableCell colSpan={8}>No Job History.</StyledTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ExecutionRecords() {
  const [pageCount, setPageCount] = useState(0);
  const [executionRecords, setExecutionRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingRows, setDownloadingRows] = useState({});
  const [retryingJobs, setRetryingJobs] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isFetching = false;
    const fetchData = () => {
      if (isFetching) return;
      isFetching = true;

      const jwt_token = localStorage.getItem("palm_care_token");
      const apiUrl = `${API_GATEWAY_URL}/jobs`;
      const headers = {
        Authorization: jwt_token,
      };
      axios
        .get(apiUrl, {
          headers,
          params: {
            page: currentPage,
          },
        })
        .then((response) => {
          setExecutionRecords(response.data.jobs);
          if (response.data.total_pages !== pageCount) {
            setPageCount(response.data.total_pages);
          }
          setIsLoading(false);
          isFetching = false;
        })
        .catch((error) => {
          console.error("Error fetching block data:", error);
          setIsLoading(false);
          isFetching = false;
        });
    };

    // Initial data fetch
    fetchData();

    // Set up a timer to periodically fetch data (e.g., every 5 minutes)
    const fetchDataInterval = setInterval(() => {
      fetchData();
    }, 10 * 1000); // Fetch data every 1 minutes (5 * 60 * 1000 milliseconds)

    return () => {
      // Clean up the interval when the component unmounts
      clearInterval(fetchDataInterval);
    };
  }, [currentPage]);

  const handleRetry = async (row) => {
    const jobId = row.id;
    setRetryingJobs((prev) => ({ ...prev, [jobId]: true }));

    try {
      const jwtToken = localStorage.getItem("palm_care_token");
      const apiUrl = `${API_GATEWAY_URL}/job/rerun/${jobId}`;

      const headers = {
        Authorization: jwtToken,
      };

      const response = await axios.post(apiUrl, {}, { headers });

      if (response.status === 200) {
        toast.success(`Job ${jobId} has been successfully retried.`);
      } else {
        toast.error(`Failed to retry job ${jobId}.`);
        setRetryingJobs((prev) => ({ ...prev, [jobId]: false }));
      }
    } catch (error) {
      console.error("Error retrying job:", error);
      setRetryingJobs((prev) => ({ ...prev, [jobId]: false }));

      if (error.response) {
        console.error("Error details:", error.response.data);
        toast.error(`Failed to retry job ${jobId}: ${error.response.data.message}`);
      } else {
        toast.error(`Failed to retry job ${jobId}.`);
      }
    }
  };

  const handlePageClick = (event, selectedPage) => {
    setCurrentPage(selectedPage);
  };

  const handleDownload = async (data, rowID) => {
    try {
      // place holder to download reports
      setDownloadingRows((prevDownloadingRows) => ({
        ...prevDownloadingRows,
        [rowID]: true,
      }));
      const job_data = data.find((row) => row.id === rowID);
      const download_url = JSON.parse(job_data.download_url);
      const container =
        download_url[Object.keys(download_url)[0]][0].split("/")[3];

      // generate azure blob storage's sas token
      let sas_token = null;
      const jwt_token = localStorage.getItem("palm_care_token");
      const apiUrl = `${API_GATEWAY_URL}/sas/download`;
      const headers = {
        Authorization: jwt_token,
      };
      await axios
        .get(apiUrl, {
          headers,
          params: {
            container: container,
          },
        })
        .then((response) => {
          sas_token = response.data.sas_url.split("?")[1];
        })
        .catch((err) => {
          console.error("Error:", err);
          throw new Error(err);
        });

      // append download link with sas token
      for (const layer of Object.keys(download_url)) {
        if (layer === "dashboard") continue;
        const download_links = download_url[layer];
        for (let i = 0; i < download_links.length; i++) {
          download_url[layer][i] = `${download_url[layer][i]}?${sas_token}`;
        }
      }

      // download into zip file
      const zip = new JSZip();
      const promises = [];
      const zip_name = `${job_data.estate}_${job_data.block
        }_${job_data.id.slice(0, 8)}.zip`;
      for (const layer of Object.keys(download_url)) {
        if (layer === "dashboard") continue;
        // Create a subdirectory for each layer
        const layerFolder = zip.folder(layer);
        const download_links = download_url[layer];
        for (let i = 0; i < download_links.length; i++) {
          let file_name = new URL(download_links[i]).pathname.split("/").pop();
          promises.push(
            axios
              .get(download_links[i], { responseType: "arraybuffer" })
              .then((response) => {
                // Check if the response status is 404
                if (response.status === 404) {
                  throw new Error(`Blob not found for ${file_name}`);
                }
                // Add the file to the subdirectory
                layerFolder.file(file_name, response.data);
              })
              .catch((error) => {
                // Handle other errors
                console.error("Error downloading blob:", error);
                throw new Error(`Failed to download blob: ${error.message}`);
              })
          );
        }
      }
      await Promise.all(promises);
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, zip_name);
      toast.success(`Successfully Downloaded Results`);
    } catch (err) {
      console.error("Error:", err);
      toast.error(
        "Failed to Download. This behavior is expected if the block data has already been removed."
      );
    } finally {
      setDownloadingRows((prevDownloadingRows) => ({
        ...prevDownloadingRows,
        [rowID]: false,
      }));
    }
  };

  const handleViewResults = (rowID) => {
    localStorage.setItem('dashboard_selected_job_id', rowID);
    window.dispatchEvent(new CustomEvent('changeTab', { detail: 'Dashboard' }));
  };

  return (
    <div className="execution__records__container">
      <CustomizedTables
        data={executionRecords}
        onClickDownload={handleDownload}
        onClickViewResults={handleViewResults}
        handleRetry={handleRetry}
        downloadingRows={downloadingRows}
        retryingJobs={retryingJobs}
        isLoading={isLoading}
      />
      <Pagination
        count={pageCount}
        color="primary"
        onChange={handlePageClick}
        style={{ display: "flex", justifyContent: "center", margin: "20px" }}
      />
    </div>
  );
}

export default ExecutionRecords;
